import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import backgroundImage from '../../../../assets/img/gallery/4.jpg';
import { Features } from '../../../../assets/features/Features';
import { useLanguage } from '../../../../context/LanguageContext';
import { useTheme } from '../../../../context/ThemeContext';
import SEO from '../../seo/SEO';
import { seoData } from '../../../../data/seoData';

const LocationPage: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialize AOS animation library
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });
  }, []);

  return (
    <>
      <SEO 
        title={seoData.location.title}
        description={seoData.location.description}
        keywords={seoData.location.keywords}
        canonical={seoData.location.canonical}
      />
      {/* Hero Section */}
      <section 
        className="location-hero d-flex align-items-center" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1 
                className="display-4 fw-bold" 
                data-aos="fade-up" 
                style={{ 
                  color: '#ffffff', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {Features.locationPage.hero.title[language]}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 mb-5 text-center" data-aos="fade-up">
              <h2 className="fw-bold">{Features.locationPage.howToArrive.title[language]}</h2>
              <p className="lead">{Features.locationPage.howToArrive.subtitle[language]}</p>
            </div>
          </div>
          
          <div className="row gy-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
              <div className={`location-info p-4 rounded shadow-sm ${theme === 'dark' ? 'bg-dark text-light' : 'bg-white'}`}>
                <h3 className="h4 mb-4">{Features.locationPage.clinicInfo.title[language]}</h3>
                
                <div className="info-item d-flex mb-3">
                  <div className="icon me-3">
                    <i className="bi bi-geo-alt-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-0">{Features.locationPage.clinicInfo.address.label[language]}</h5>
                    <p className="mb-0">{Features.locationPage.clinicInfo.address.value[language].split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < Features.locationPage.clinicInfo.address.value[language].split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}</p>
                  </div>
                </div>
                
                <div className="info-item d-flex mb-3">
                  <div className="icon me-3">
                    <i className="bi bi-clock-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-0">{Features.locationPage.clinicInfo.openingHours.label[language]}</h5>
                    <p className="mb-0">
                      {Features.locationPage.clinicInfo.openingHours.value[language].split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < Features.locationPage.clinicInfo.openingHours.value[language].split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                </div>
                
                <div className="info-item d-flex mb-3">
                  <div className="icon me-3">
                    <i className="bi bi-telephone-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-0">{Features.locationPage.clinicInfo.contact.label[language]}</h5>
                    <p className="mb-0">
                      {Features.locationPage.clinicInfo.contact.value[language].split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < Features.locationPage.clinicInfo.contact.value[language].split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                      <span
                        className="phone-info-icon"
                        data-tooltip-text={language === 'en' ? 'Call to a national landline' : 'Chamada para a rede fixa nacional'}
                      >
                        <i className="bi bi-info-circle-fill"></i>
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="info-item d-flex">
                  <div className="icon me-3">
                    <i className="bi bi-envelope-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-0">{Features.locationPage.clinicInfo.email.label[language]}</h5>
                    <p className="mb-0">{Features.locationPage.clinicInfo.email.value[language]}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6" data-aos="fade-left" data-aos-delay="200">
              <div className="map-container rounded shadow-sm overflow-hidden">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3003.3590764860673!2d-8.56701!3d41.2119!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2465e2faa85e99%3A0x5c2a7e1d6b3a0a1d!2sAv.%20Eng.%20Duarte%20Pacheco%2C%20642%2C%204445-416%20Ermesinde!5e0!3m2!1spt-PT!2spt!4v1625584000000!5m2!1spt-PT!2spt" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy"
                  title={Features.locationPage.hero.title[language]}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directions Section */}
      <section className={`directions-section py-5 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
        <div className="container">
          <div className="row">
            <div className="col-12 mb-5 text-center" data-aos="fade-up">
              <h2 className="fw-bold">{Features.locationPage.directions.title[language]}</h2>
              <p className="lead">{Features.locationPage.directions.subtitle[language]}</p>
            </div>
          </div>
          
          <div className="row justify-content-center">
            <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
              <div className={`directions-info p-4 rounded shadow-sm ${theme === 'dark' ? 'bg-secondary text-light' : 'bg-white'}`} style={{ 
                backgroundColor: theme === 'dark' ? '#2a2c2e' : '' 
              }}>
                <div className="direction-item mb-4">
                  <h4 className="h5 mb-3">
                    <i className="bi bi-car-front-fill text-primary me-2"></i>
                    {Features.locationPage.directions.byCar.title[language]}
                  </h4>
                  <p>
                    {Features.locationPage.directions.byCar.description[language]}
                  </p>
                  <p className="fw-bold mb-0">
                    {Features.locationPage.directions.byCar.exitInfo[language].split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < Features.locationPage.directions.byCar.exitInfo[language].split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                
                <div className="direction-item mb-4">
                  <h4 className="h5 mb-3">
                    <i className="bi bi-bus-front-fill text-primary me-2"></i>
                    {Features.locationPage.directions.byPublicTransport.title[language]}
                  </h4>
                  <p>
                    {Features.locationPage.directions.byPublicTransport.description[language]}
                  </p>
                </div>
                
                <div className="direction-item">
                  <h4 className="h5 mb-3">
                    <i className="bi bi-shop text-primary me-2"></i>
                    {Features.locationPage.directions.nearbyServices.title[language]}
                  </h4>
                  <p>
                    {Features.locationPage.directions.nearbyServices.description[language]}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LocationPage; 